// Recrutement — helpers & petites briques partagées (port du proto
// design/recrutement rec-data.jsx / rec-styles.css, scoped .recapp).
import type { ReactNode } from 'react';
import type { RecApprovalDTO, RecReqStatus, RecRequisitionDTO } from '../../api/hooks.js';

// Libellé + couleur par statut de demande (§6.1).
export const REC_ST: Record<RecReqStatus, { lbl: string; cls: 'info' | 'warn' | 'green' | 'shf' | 'bad' | 'mute' }> = {
  brouillon:  { lbl: 'Brouillon — à re-soumettre', cls: 'bad' },
  en_attente: { lbl: 'En attente de validation', cls: 'warn' },
  validee:    { lbl: 'Validée — annonce à publier', cls: 'info' },
  ouverte:    { lbl: 'Ouverte', cls: 'green' },
  pourvue:    { lbl: 'Pourvue', cls: 'shf' },
  gelee:      { lbl: 'Gelée', cls: 'mute' },
  annulee:    { lbl: 'Annulée', cls: 'mute' },
};

export const recFmtMoney = (n: number) => `${n.toLocaleString('fr-FR')} DZD`;

// Les 12 templates d'un rôle — libellé, glyphe, sous-titre (ordre
// canonique du serveur, TPL_KEYS de recrutementCatalogue.ts).
export const TPL_META: Record<string, [string, string, string]> = {
  fichePoste: ['Fiche de poste', '📋', 'définit missions & responsabilités'],
  annonce: ['Annonce', '📢', 'publiée après validation de la demande'],
  presel: ['Critères présélection', '🔍', 'PASS / FAIL — critères éliminatoires ou indicatifs'],
  script: ['Script d’appel', '📞', 'script SHIFTIN en étapes, relances par rôle'],
  kits: ['Kit d’entretien', '🧰', 'déroulé + questions par type d’entretien'],
  grid: ['Grille d’évaluation', '⭐', 'critères pondérés par dimension — prête quand les 4 sont couvertes'],
  test: ['Cas pratique', '🧪', 'brief, livrables, délai 48/72 h, rubrique par dimension'],
  offre: ['Modèle d’offre', '✉️', 'PDF variable — salaire, essai, avantages'],
  dossier: ['Dossier admin', '🗂', 'pièces légales par entité & contrat'],
  onboarding: ['Plan onboarding', '🧳', 'J1 → J90, tâches par responsable'],
  oneOnOne: ['Plan de 1:1', '🗓', 'J+7 · J+30 · J+60 · J+90'],
  ressources: ['Ressources', '📚', 'handbook, accès outils, brand book'],
};

export const recContrat = (r: Pick<RecRequisitionDTO, 'contractType' | 'cddMonths'>) =>
  r.contractType === 'CDI' ? 'CDI' : `CDD ${r.cddMonths ?? '?'} mois`;

export const recFmtDate = (s: string) => s.split('-').reverse().join('/');

// ── champ restreint (salaire) ───────────────────────────────
export function RecSal({ band, children }: { band: [number, number] | null; children?: ReactNode }) {
  if (band == null && children == null) {
    return <span className="rec-lockpill" title="Champ restreint — visible RH & direction uniquement">🔒 Confidentiel</span>;
  }
  return <b className="rec-sal">{children ?? `${recFmtMoney(band![0])} – ${recFmtMoney(band![1])}`}</b>;
}

// ── chaîne de validation (rec-chain du proto) ───────────────
export function RecChain({ approvals }: { approvals: RecApprovalDTO[] }) {
  return (
    <div className="rec-chain">
      {approvals.map((a) => {
        const cls = a.status === 'validee' ? 'ok' : a.status === 'courante' ? 'now' : a.status === 'refusee' ? 'ko' : '';
        return (
          <div key={a.step} className={`rec-chstep ${cls}`}>
            <span className="pt"></span>
            <div>
              <div className="w">{a.roleLabel}</div>
              <div className="s">
                {a.status === 'validee' && <>validé{a.decider ? ` par ${a.decider}` : ''}{a.decidedAt ? ` · ${recFmtDate(a.decidedAt)}` : ''}</>}
                {a.status === 'courante' && 'à cette étape de décider'}
                {a.status === 'en_attente' && 'en attente'}
                {a.status === 'refusee' && <>refusé{a.decider ? ` par ${a.decider}` : ''}{a.comment ? ` — « ${a.comment} »` : ''}</>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── jauge fourchette (rec-band du proto) ────────────────────
export function RecBand({ band, val }: { band: [number, number]; val: number }) {
  const [lo, hi] = band;
  const span = Math.max(hi - lo, 1);
  const min = lo - span * 0.35;
  const max = hi + span * 0.35;
  const pct = (x: number) => Math.min(98, Math.max(2, ((x - min) / (max - min)) * 100));
  const out = val < lo || val > hi;
  return (
    <div className="rec-band">
      <div className="rail"></div>
      <div className="zone" style={{ left: `${pct(lo)}%`, width: `${pct(hi) - pct(lo)}%` }}></div>
      <div className={`mk${out ? ' out' : ''}`} style={{ left: `${pct(val)}%` }}></div>
      <span className="bl" style={{ left: `${pct(lo)}%` }}>{recFmtMoney(lo)}</span>
      <span className="bl" style={{ left: `${pct(hi)}%` }}>{recFmtMoney(hi)}</span>
    </div>
  );
}

// ── pipeline (phase 2) ──────────────────────────────────────
import { REC_STAGES } from '@pointage/shared';
import type { RecApplicationDTO, RecStageId } from '../../api/hooks.js';

export const REC_STAGE_META = new Map(REC_STAGES.map((s) => [s.id as string, s]));
export const recStageOf = (id: string) => REC_STAGE_META.get(id) ?? { id, lbl: id, c: '#74747e' };

/** Jours passés dans l'étape courante. */
export function recDays(stageSince: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(`${stageSince}T00:00:00`).getTime()) / 864e5));
}

export function RecDaysPill({ since, suffix }: { since: string; suffix?: string }) {
  const d = recDays(since);
  return <span className={`rec-days${d > 9 ? ' bad' : d > 4 ? ' warn' : ''}`}>{d} j{suffix ?? ''}</span>;
}

export function RecEstPill({ stage }: { stage: RecStageId }) {
  const st = recStageOf(stage);
  return (
    <span className="rec-est" style={{ background: `color-mix(in srgb,${st.c} 14%,transparent)`, color: st.c }}>
      <i></i>{st.lbl}
    </span>
  );
}

export function RecAv({ app, sm, big }: { app: RecApplicationDTO; sm?: boolean; big?: boolean }) {
  const c = app.candidate;
  const initials = `${c.firstName[0] ?? ''}${c.lastName[0] ?? ''}`;
  if (big) return <span className="r3-bigav" style={{ background: c.avColor }}>{initials}</span>;
  return <span className={`rec-av${sm ? ' sm' : ''}`} style={{ background: c.avColor }}>{initials}</span>;
}

// ── offres (phase 4) ────────────────────────────────────────
import { REC_OFFER_STATUS } from '@pointage/shared';
import type { RecOfferDTO } from '../../api/hooks.js';

export const REC_OFFER_ST = new Map(REC_OFFER_STATUS.map((s) => [s.id as string, s]));
export const recOfferStOf = (id: string) => REC_OFFER_ST.get(id) ?? { id, lbl: id, c: '#74747e' };

/** Version courante d'une candidature (la dernière). */
export const recOfferOf = (a: RecApplicationDTO): RecOfferDTO | null =>
  a.offers.length ? a.offers[a.offers.length - 1]! : null;

/** Jours restants avant la deadline de réponse (négatif = dépassée). */
export function recOfferDaysLeft(o: RecOfferDTO): number | null {
  if (o.status !== 'ENVOYEE' || !o.responseDeadline) return null;
  return Math.round((new Date(`${o.responseDeadline}T00:00:00`).getTime() - Date.now()) / 864e5);
}

/** Prochaine action côté équipe sur une offre — null si rien à faire. */
export function recOfferAction(o: RecOfferDTO): string | null {
  switch (o.status) {
    case 'BROUILLON': return o.refusalComment ? 'Refusée en interne — ajuster puis re-soumettre' : 'Brouillon — soumettre à validation';
    case 'EN_ATTENTE': return null; // la balle est chez les valideurs
    case 'VALIDEE': return 'Validée — envoyer au candidat (PDF)';
    case 'ENVOYEE': {
      const d = recOfferDaysLeft(o);
      return d != null && d < 0 ? 'Deadline dépassée — relancer ou trancher' : null;
    }
    case 'CONTRE': return 'Contre-proposition — construire la version suivante';
    default: return null;
  }
}

/** « Prochaine action » (rec2Next). */
export function recNextAction(a: RecApplicationDTO): string {
  switch (a.stage) {
    case 'NOUVELLE': return 'Présélection CV à faire';
    case 'PRESEL': return 'Terminer la checklist de présélection';
    case 'APPEL':
      return a.call?.verdict === 'stop' ? 'Verdict d’appel « rejeter » — motif à poser'
        : a.call?.reportedAt ? `Appel fait le ${new Date(a.call.reportedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} par ${a.call.reportedBy ?? '—'} — à vérifier, puis verdict`
        : a.call?.scheduledAt ? 'Appel planifié — mener puis clôturer'
          : 'Appel de qualification à planifier';
    case 'TEST':
      return a.test?.status === 'RENDU' ? 'Cas pratique rendu — à noter'
        : a.test?.status === 'ENVOYE' ? 'Cas pratique en cours — rendu attendu'
          : a.test?.status === 'NOTE' ? (a.gates.test.pass ? 'Cas réussi — passer en entretien' : 'Cas sous le seuil — rejeter')
            : 'Cas pratique à envoyer';
    case 'ENTRETIEN': {
      const done = a.interviews.filter((i) => i.status === 'REALISE');
      const missing = done.reduce((n, i) => n + Math.max(i.interviewers.length - i.scorecardCount, 0), 0);
      if (missing > 0) return `${missing} scorecard${missing > 1 ? 's' : ''} en attente du jury`;
      if (!a.interviews.some((i) => i.status === 'PLANIFIE' || i.status === 'REALISE')) return 'Entretien à planifier';
      return a.interviews.some((i) => i.status === 'PLANIFIE') ? 'Entretien planifié — à réaliser' : 'Jury complet — avancer ou trancher';
    }
    case 'FINALISTE': {
      const o = recOfferOf(a);
      if (o) return recOfferAction(o) ?? 'Offre en cours';
      if (!a.gates.verif.ok) return 'Vérification à mener (owner)';
      if (a.gates.bar?.state === 'ko' && !a.gates.overridden) return 'Sous la barre — décision owner';
      return a.gates.open ? 'Construire l’offre' : 'Portes de l’offre à ouvrir';
    }
    case 'OFFRE': {
      const o = recOfferOf(a);
      if (!o) return 'Offre à construire';
      return recOfferAction(o)
        ?? (o.status === 'EN_ATTENTE' ? `Offre v${o.version} en validation` : 'Réponse candidat attendue');
    }
    case 'RECRUTE': return 'Onboarding en cours';
    case 'VIVIER': return 'Au vivier — repêchable';
    case 'REJETE': return a.rejectMotif ? `Rejeté — ${a.rejectMotif}` : 'Rejeté';
    default: return '—';
  }
}

// ── pastille entité (rec-ent) ───────────────────────────────
export function RecEnt({ id, name, color }: { id: string; name: string; color: string }) {
  return (
    <span className="rec-ent" style={{ '--ec': color } as React.CSSProperties} data-ent={id}>
      <i></i>{name}
    </span>
  );
}
